import { DisplayTheme, DisplayThemeValueSet } from '../../types';
import { BASE_COLORS, highlightColorDark, highlightColorLight, U_ALPHA_DARK, U_ALPHA_LIGHT } from './baseColors';
import { type FunctionalThemeColors, FUNCTIONAL_COLORS } from './functionalColors';

export type BaseComponents = {
  documentPlaceholderCoverTitle: FunctionalThemeColors['secondaryText'];
  documentPlaceholderCoverSubtitle: FunctionalThemeColors['textTertiary'];
  playbackControlsBg: FunctionalThemeColors['backgroundPrimary'];
  buttonSecondaryDefaultColor: FunctionalThemeColors['borderTertiaryAlpha'];
  annotationBarIconColor: FunctionalThemeColors['iconSecondary'];
  bottomSheetBackground: FunctionalThemeColors['backgroundElevated'];
  documentRowItemSeparator: FunctionalThemeColors['bgCanvas'];
  popupIconTintColor: DisplayThemeValueSet;

  annotationBarPopoverDeleteButtonBackground: DisplayThemeValueSet;
  readerBackground: DisplayThemeValueSet;
  readerBackgroundOpaque: DisplayThemeValueSet;
  readerOverlay: DisplayThemeValueSet;
  readerHeaderBorder: DisplayThemeValueSet;
  readingTitleText: DisplayThemeValueSet;
  highlightColorTop: DisplayThemeValueSet;
  highlightColorBottom: DisplayThemeValueSet;
  highlightTextColor: DisplayThemeValueSet;
  inboxDropShadow: DisplayThemeValueSet;
  infoMetadataTitle: DisplayThemeValueSet;
  infoMetadataValue: DisplayThemeValueSet;
  infoCountBackground: DisplayThemeValueSet;
  tagBackgroundColor: DisplayThemeValueSet;
  buttonPrimaryDefaultColor: DisplayThemeValueSet;
  buttonPrimaryDefaultColorInverse: DisplayThemeValueSet;
  currentlyReadingBackground: DisplayThemeValueSet;
  progressBarBackground: DisplayThemeValueSet;
  progressBarScrollBackground: DisplayThemeValueSet;
  activityIndicatorColor: DisplayThemeValueSet;
  toastBackground: DisplayThemeValueSet;
  toastText: DisplayThemeValueSet;
  toastButtonText: DisplayThemeValueSet;
  selection: DisplayThemeValueSet;
  ttsIndicator: DisplayThemeValueSet;
  ttsZIndex: DisplayThemeValueSet<number>;
  ttsMixBlendMode: DisplayThemeValueSet;
  ttsTransition: DisplayThemeValueSet;
  teleprompt: DisplayThemeValueSet;
  inboxTabBarSelection: DisplayThemeValueSet;
  findInDocumentHighlight: DisplayThemeValueSet;
  findInDocumentHighlightActive: DisplayThemeValueSet;
  digestNavButton: DisplayThemeValueSet;
  pdfDigestBackground: DisplayThemeValueSet;
  settingsBackground: DisplayThemeValueSet;
  listItemBackground: DisplayThemeValueSet;
  pageShadow: DisplayThemeValueSet;
  pageBorderBackground: DisplayThemeValueSet;
  readerSettingsMenuGradientColor: DisplayThemeValueSet;
  switchButton: DisplayThemeValueSet;
  dailyReviewBackground: DisplayThemeValueSet;
  subMenuSelectorBackground: DisplayThemeValueSet;
  chunkLoadingSpinnerFilter: DisplayThemeValueSet;
  nextChunkArrowFilter: DisplayThemeValueSet;
  readwiseLogo: DisplayThemeValueSet;
  shouldShowDocumentPlaceholderBackground: DisplayThemeValueSet<boolean>;
};

export const BASE_COMPONENTS: BaseComponents = {
  documentPlaceholderCoverTitle: FUNCTIONAL_COLORS.secondaryText,
  documentPlaceholderCoverSubtitle: FUNCTIONAL_COLORS.textTertiary,
  playbackControlsBg: {
    ...FUNCTIONAL_COLORS.backgroundPrimary,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.neutral97,
      [DisplayTheme.Dark]: BASE_COLORS.neutral00,
    },
  },
  buttonSecondaryDefaultColor: FUNCTIONAL_COLORS.borderTertiaryAlpha,
  annotationBarIconColor: {
    ...FUNCTIONAL_COLORS.iconSecondary,
    bookwise: {
      ...FUNCTIONAL_COLORS.iconSecondary,
    },
  },
  bottomSheetBackground: {
    ...FUNCTIONAL_COLORS.backgroundElevated,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone97,
      [DisplayTheme.Dark]: BASE_COLORS.stone10,
    },
  },
  documentRowItemSeparator: {
    ...FUNCTIONAL_COLORS.bgCanvas,
    highContrast: {
      [DisplayTheme.Light]: '#dadada',
    },
  },
  popupIconTintColor: {
    [DisplayTheme.Light]: '#1C1C1E',
    [DisplayTheme.Dark]: '#F2F2F7',
  },

  dailyReviewBackground: {
    [DisplayTheme.Light]: '#f1f5f8',
    [DisplayTheme.Dark]: '#f1f5f8',
  },
  annotationBarPopoverDeleteButtonBackground: {
    [DisplayTheme.Light]: BASE_COLORS.red97,
    [DisplayTheme.Dark]: 'rgba(244, 143, 139, 0.1)',
  },
  readingTitleText: {
    [DisplayTheme.Light]: BASE_COLORS.neutral05,
    [DisplayTheme.Dark]: BASE_COLORS.neutral20,
  },
  readerBackground: {
    [DisplayTheme.Light]: BASE_COLORS.neutral97,
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
    highContrast: {
      [DisplayTheme.Light]: 'white',
      [DisplayTheme.Dark]: 'black',
    },
  },
  readerBackgroundOpaque: {
    [DisplayTheme.Light]: `rgba(249, 249, 250, 0.1)`,
    [DisplayTheme.Dark]: 'rgba(0,0,0,0.01)',
    highContrast: {
      [DisplayTheme.Light]: 'white',
      [DisplayTheme.Dark]: '#000000',
    },
  },
  readerOverlay: {
    [DisplayTheme.Light]: 'rgba(0,0,0,1)',
    [DisplayTheme.Dark]: 'rgba(0,0,0,0.75)',
  },
  readerHeaderBorder: {
    [DisplayTheme.Light]: '#E4E6EA',
    [DisplayTheme.Dark]: BASE_COLORS.darkPrimaryLightest,
  },
  inboxDropShadow: {
    [DisplayTheme.Light]: BASE_COLORS.veryDarkGrey,
    [DisplayTheme.Dark]: 'black',
  },
  infoMetadataTitle: {
    [DisplayTheme.Light]: '#B1B6BB',
    [DisplayTheme.Dark]: '#ABABAB',
  },
  infoMetadataValue: {
    [DisplayTheme.Light]: '#4B4D52',
    [DisplayTheme.Dark]: '#E8E8E8',
  },
  infoCountBackground: {
    [DisplayTheme.Light]: '#ECECEC',
    [DisplayTheme.Dark]: '#445064',
  },
  tagBackgroundColor: {
    [DisplayTheme.Light]: BASE_COLORS.lightGrey,
    [DisplayTheme.Dark]: 'rgba(255,255,255,0.1)',
  },
  highlightColorTop: {
    [DisplayTheme.Light]: highlightColorLight,
    [DisplayTheme.Dark]: highlightColorDark,
  },
  highlightColorBottom: {
    [DisplayTheme.Light]: highlightColorLight,
    [DisplayTheme.Dark]: 'rgba(0,178,255,0.36)',
  },
  highlightTextColor: {
    [DisplayTheme.Light]: 'black',
    [DisplayTheme.Dark]: 'white',
  },
  buttonPrimaryDefaultColor: {
    [DisplayTheme.Light]: 'rgba(24, 90, 189, 0.06)',
    [DisplayTheme.Dark]: 'rgba(51, 108, 255, 0.26)',
  },
  buttonPrimaryDefaultColorInverse: {
    [DisplayTheme.Light]: 'rgba(51, 108, 255, 0.26)',
    [DisplayTheme.Dark]: 'rgba(24, 90, 189, 0.06)',
  },
  progressBarBackground: {
    [DisplayTheme.Light]: BASE_COLORS.indigo40,
    [DisplayTheme.Dark]: BASE_COLORS.purple50,
    bookwise: {
      [DisplayTheme.Light]: '#28313B1A',
      [DisplayTheme.Dark]: BASE_COLORS.stone15,
    },
  },
  progressBarScrollBackground: {
    [DisplayTheme.Light]: '#b8b9bd',
    [DisplayTheme.Dark]: BASE_COLORS.neutral65,
    bookwise: {
      [DisplayTheme.Light]: '#FF69004D',
      [DisplayTheme.Dark]: '#FF690080',
    },
  },
  currentlyReadingBackground: {
    [DisplayTheme.Light]: '#FAFCFF',
    [DisplayTheme.Dark]: BASE_COLORS.darkPrimaryLighter,
  },
  activityIndicatorColor: {
    [DisplayTheme.Light]: BASE_COLORS.darkGrey,
    [DisplayTheme.Dark]: BASE_COLORS.white,
  },
  toastBackground: {
    [DisplayTheme.Light]: BASE_COLORS.neutral10,
    [DisplayTheme.Dark]: BASE_COLORS.neutral100,
  },
  toastText: {
    [DisplayTheme.Light]: '#F9F9FA',
    [DisplayTheme.Dark]: '#28313B',
  },
  toastButtonText: {
    [DisplayTheme.Light]: '#AEC8F1',
    [DisplayTheme.Dark]: '#376EF2',
  },
  selection: {
    [DisplayTheme.Light]: 'rgba(55,110,242,0.25)',
    [DisplayTheme.Dark]: 'rgba(89,142,244,0.35)',
    highContrast: {
      [DisplayTheme.Light]: 'rgb(55,110,242)',
      [DisplayTheme.Dark]: 'rgb(115,157,245)',
    },
  },
  ttsIndicator: {
    [DisplayTheme.Light]: 'rgba(55,110,242,0.25)',
    [DisplayTheme.Dark]: 'rgba(89,142,244,0.35)',
    highContrast: {
      [DisplayTheme.Light]: 'rgb(55,110,242)',
      [DisplayTheme.Dark]: 'rgb(115,157,245)',
    },
  },
  ttsZIndex: {
    [DisplayTheme.Light]: 1,
    [DisplayTheme.Dark]: 1,
  },
  ttsMixBlendMode: {
    [DisplayTheme.Light]: 'normal',
    [DisplayTheme.Dark]: 'normal',
  },
  ttsTransition: {
    [DisplayTheme.Light]: 'transform 0.2s, width 0.2s, top 0.2s',
    [DisplayTheme.Dark]: 'transform 0.2s, width 0.2s, top 0.2s',
  },
  teleprompt: {
    [DisplayTheme.Light]: '#376EF240',
    [DisplayTheme.Dark]: '#598EF459',
  },
  inboxTabBarSelection: {
    [DisplayTheme.Light]: U_ALPHA_LIGHT.uAlpha400,
    [DisplayTheme.Dark]: U_ALPHA_DARK.uAlpha400,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.blue70,
      [DisplayTheme.Dark]: BASE_COLORS.blue30,
    },
  },
  findInDocumentHighlight: {
    [DisplayTheme.Light]: 'rgba(108, 120, 131, 0.1)',
    [DisplayTheme.Dark]: 'rgba(108, 120, 131, 0.42)',
    highContrast: {
      [DisplayTheme.Light]: 'rgb(131, 161, 224, 1.0)',
      [DisplayTheme.Dark]: 'rgba(69, 146, 238, 1.0)',
    },
  },
  findInDocumentHighlightActive: {
    [DisplayTheme.Light]: 'rgba(38, 117, 222, 0.24)',
    [DisplayTheme.Dark]: 'rgba(69, 146, 238, 0.3)',
  },
  digestNavButton: {
    [DisplayTheme.Light]: BASE_COLORS.purple40,
    [DisplayTheme.Dark]: BASE_COLORS.purple60,
  },
  pdfDigestBackground: {
    [DisplayTheme.Light]: BASE_COLORS.neutral90,
    [DisplayTheme.Dark]: BASE_COLORS.neutral20,
  },
  settingsBackground: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone97,
      [DisplayTheme.Dark]: BASE_COLORS.stone10,
    },
  },
  listItemBackground: {
    [DisplayTheme.Light]: U_ALPHA_LIGHT.uAlphaNeutral200,
    [DisplayTheme.Dark]: U_ALPHA_DARK.uAlphaNeutral200,
    highContrast: {
      [DisplayTheme.Light]: '#dadada',
    },
  },
  pageShadow: {
    [DisplayTheme.Light]: 'rgba(12, 17, 23, 0.27)',
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
  },
  pageBorderBackground: {
    [DisplayTheme.Light]: 'rgba(210,210,210,0.26)',
    [DisplayTheme.Dark]: BASE_COLORS.neutral00,
  },
  readerSettingsMenuGradientColor: {
    [DisplayTheme.Light]: `${BASE_COLORS.indigo40}20`,
    [DisplayTheme.Dark]: `${BASE_COLORS.purple60}0A`,
  },
  switchButton: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral100,
    highContrast: {
      [DisplayTheme.Light]: BASE_COLORS.neutral80,
      [DisplayTheme.Dark]: BASE_COLORS.neutral90,
    },
  },
  subMenuSelectorBackground: {
    [DisplayTheme.Light]: BASE_COLORS.neutral100,
    [DisplayTheme.Dark]: BASE_COLORS.neutral25,
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone97,
      [DisplayTheme.Dark]: BASE_COLORS.stone25,
    },
  },
  chunkLoadingSpinnerFilter: {
    [DisplayTheme.Light]: '',
    [DisplayTheme.Dark]: 'invert(1)',
  },
  nextChunkArrowFilter: {
    [DisplayTheme.Light]: 'brightness(0) saturate(100%) invert(42%) sepia(23%) saturate(261%) hue-rotate(169deg) brightness(91%) contrast(84%);',
    [DisplayTheme.Dark]: 'brightness(0) saturate(100%) invert(68%) sepia(12%) saturate(300%) hue-rotate(171deg) brightness(91%) contrast(90%);',
  },
  readwiseLogo: {
    [DisplayTheme.Light]: BASE_COLORS.black,
    [DisplayTheme.Dark]: BASE_COLORS.white,
  },
  shouldShowDocumentPlaceholderBackground: {
    [DisplayTheme.Light]: true,
    [DisplayTheme.Dark]: true,
    bookwise: {
      [DisplayTheme.Light]: false,
      [DisplayTheme.Dark]: false,
    },
  },
};
